import { QueueItem } from '@/api/callcenter/dashboard';
import { QueueCard } from '@/features/callcenter/components/QueueCard';
import { QueueCardSkeleton } from '@/features/callcenter/components/QueueCardSkeleton';
import { useQueueCountdowns } from '@/features/callcenter/hooks/useQueueCountdowns';
import { BrightPatternStatus } from '@/features/callcenter/lib/brightpattern';
import { deriveSlaDisplay } from '@/features/callcenter/logic';
import useCallCenterStore from '@/stores/callcenter';
import { InboxIcon } from '@heroicons/react/24/outline';
import { useShallow } from 'zustand/react/shallow';

// The queue is push-based (leads arrive automatically, there is nothing to
// browse or search for), so NoResults' generic "No X found..." copy reads
// wrong here -- this is a dedicated, self-contained empty state rather than
// a reused template.
const QueueEmptyState = () => (
  <div
    data-testid="queue-empty-state"
    className="m-3 flex h-80 flex-col items-center justify-center gap-1 rounded border border-dashed text-center text-foreground/50"
  >
    <InboxIcon className="h-16 w-16" aria-hidden="true" />
    <p className="font-medium text-foreground/70">Your queue is empty</p>
    <p className="text-sm">New leads appear here automatically — keep BrightPattern open.</p>
  </div>
);

const QueueListSkeleton = () => (
  <div className="space-y-3">
    <QueueCardSkeleton />
    <QueueCardSkeleton />
    <QueueCardSkeleton />
  </div>
);

type QueueListProps = {
  queue: QueueItem[];
  dataUpdatedAt: number;
  isLoading?: boolean;
  // The viewing agent's own on-call state: an offer with no accept deadline
  // shows "Starts after your call" instead of "Clock paused" while they are
  // on the line (see deriveSlaDisplay).
  agentOnCall?: boolean;
  onOpenItem?: (item: QueueItem) => void;
  onReleaseItem?: (item: QueueItem) => void;
  onDialItem?: (item: QueueItem) => void;
  dialStatus?: BrightPatternStatus;
  pendingAssignmentId?: number | null;
};

export const QueueList = ({
  queue,
  dataUpdatedAt,
  isLoading = false,
  agentOnCall = false,
  onOpenItem,
  onReleaseItem,
  onDialItem,
  dialStatus,
  pendingAssignmentId = null,
}: QueueListProps) => {
  // Always called, even on the loading/empty early-returns below, so the
  // ticker's identity (and 1s interval) survives those state transitions.
  const elapsedSeconds = useQueueCountdowns(dataUpdatedAt);
  const { highlightedAssignmentId } = useCallCenterStore(
    useShallow((state) => ({ highlightedAssignmentId: state.highlightedAssignmentId })),
  );

  if (isLoading) {
    return <QueueListSkeleton />;
  }

  if (queue.length === 0) {
    return <QueueEmptyState />;
  }

  return (
    <div data-testid="queue-list" className="space-y-3">
      {/* Server order is priority order -- never sort, filter, or paginate here. */}
      {queue.map((item) => (
        <QueueCard
          key={item.assignment_id}
          item={item}
          display={deriveSlaDisplay(item, elapsedSeconds, new Date(), agentOnCall)}
          onOpen={onOpenItem}
          onRelease={onReleaseItem}
          onDial={onDialItem}
          dialStatus={dialStatus}
          isPending={item.assignment_id === pendingAssignmentId}
          isHighlighted={item.assignment_id === highlightedAssignmentId}
        />
      ))}
    </div>
  );
};
